import { useState, useRef } from "react";
import { useSuccessSound } from "@/hooks/useSuccessSound";
import { Copy, Check, Phone, Car, Sparkles, Wifi, Calendar, User, Mail, Loader2, Download, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import SuccessSparkles from "@/components/SuccessSparkles";
import html2canvas from "html2canvas";

interface WeatherData {
  temperature: number;
  feelsLike: number;
  description: string;
  humidity: number;
  wind: string;
}

interface AIInsights {
  pricingAnalysis?: {
    explanation: string;
  };
  tripInsights?: {
    routeNotes: string;
    trafficEstimate?: string;
  };
  tips?: string[];
}

interface QuoteData {
  pickupAddress: string;
  dropoffAddress: string;
  pickupDate: string;
  pickupTime: string;
  distance: number;
  duration: number;
  total: number;
  additionalStops?: number;
  pricingMethod?: 'flat-rate' | 'local' | 'formula';
  flatRateDestination?: string;
  gratuityIncluded?: boolean;
  // Pricing breakdown fields
  baseFare?: number;
  mileageCost?: number;
  timeCost?: number;
  additionalStopsCost?: number;
  lateNightSurcharge?: number;
  gratuity?: number;
}

interface LuxuryQuotePreviewCardProps {
  quote: QuoteData;
  weather?: WeatherData | null;
  aiInsights?: AIInsights | null;
  onClose?: () => void;
}

const AMENITIES = [
  "Licensed Chauffeur",
  "Flight Tracking",
  "Bottled Water",
  "Meet & Greet",
];

const LuxuryQuotePreviewCard = ({ 
  quote, 
  weather, 
  aiInsights,
  onClose 
}: LuxuryQuotePreviewCardProps) => {
  const { playSuccessSound } = useSuccessSound();
  const [copyStatus, setCopyStatus] = useState('');
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const quoteCardRef = useRef<HTMLDivElement>(null);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = numbers.slice(0, 10);
    
    // Format as (XXX) XXX-XXXX
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setBookingForm(prev => ({ ...prev, phone: formatted }));
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!bookingForm.name.trim() || !bookingForm.email.trim() || !bookingForm.phone.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bookingForm.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    // Phone validation - must have 10 digits
    const phoneDigits = bookingForm.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          name: bookingForm.name.trim(),
          email: bookingForm.email.trim(),
          phone: bookingForm.phone.trim(),
          pickupDate: quote.pickupDate,
          pickupTime: formatTime(quote.pickupTime),
          pickupLocation: quote.pickupAddress,
          dropoffLocation: quote.dropoffAddress,
          occasion: 'Quote Request',
          passengers: '',
          message: `Quote Total: $${quote.total.toFixed(2)} (20% gratuity included)\nDistance: ${quote.distance.toFixed(1)} miles\nTravel Time: ${Math.round(quote.duration)} minutes${quote.lateNightSurcharge && quote.lateNightSurcharge > 0 ? `\nLate-Night/Early Morning Surcharge: $${quote.lateNightSurcharge.toFixed(2)}` : ''}`,
          pricingBreakdown: {
            total: quote.total,
            distance: quote.distance,
            duration: quote.duration,
            baseFare: quote.baseFare,
            mileageCost: quote.mileageCost,
            timeCost: quote.timeCost,
            additionalStopsCost: quote.additionalStopsCost,
            lateNightSurcharge: quote.lateNightSurcharge,
            gratuity: quote.gratuity,
            pricingMethod: quote.pricingMethod,
            flatRateDestination: quote.flatRateDestination,
          },
        },
      });

      if (error) throw error;

      // Trigger success animation and sound
      setShowSuccessAnimation(true);
      playSuccessSound();
      setTimeout(() => setShowSuccessAnimation(false), 1500);

      toast({
        title: "Booking Request Sent! ✓",
        description: "A team member will review your quote and contact you shortly.",
      });

      setShowBookingDialog(false);
      setBookingForm({ name: '', email: '', phone: '' });
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error sending booking request:', error);
      toast({
        title: "Error",
        description: "Failed to send booking request. Please try calling (210) 296-7409.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateQuoteText = () => {
    let quoteText = `═══════════════════════════════
RICK'S BLACK CAR SERVICE
Executive Transportation
═══════════════════════════════

✦ YOUR QUOTE ✦

◉ PICKUP
${quote.pickupAddress || 'San Antonio, TX'}

◉ DESTINATION
${quote.dropoffAddress}

◉ SCHEDULE
${formatDate(quote.pickupDate)} at ${formatTime(quote.pickupTime)}

◉ DISTANCE
${quote.distance.toFixed(1)} miles

◉ ESTIMATED TRAVEL TIME
${Math.round(quote.duration)} minutes${quote.additionalStops && quote.additionalStops > 0 ? `

◉ ADDITIONAL STOPS: ${quote.additionalStops}` : ''}

───────────────────────────────
TOTAL: $${quote.total.toFixed(2)}
───────────────────────────────
(20% Gratuity Included)`;

    if (weather) {
      quoteText += `

☁️ PICKUP WEATHER CONDITIONS
Temperature: ${weather.temperature}°F
Feels Like: ${weather.feelsLike}°F
Conditions: ${weather.description}
Humidity: ${weather.humidity}%
Wind: ${weather.wind}`;
    }

    if (aiInsights) {
      quoteText += `

🤖 AI TRIP INSIGHTS`;

      if (aiInsights.pricingAnalysis?.explanation) {
        quoteText += `
Pricing: ${aiInsights.pricingAnalysis.explanation}`;
      }

      if (aiInsights.tripInsights?.routeNotes) {
        quoteText += `
Route: ${aiInsights.tripInsights.routeNotes}`;
      }

      if (aiInsights.tripInsights?.trafficEstimate) {
        quoteText += `
Traffic: ${aiInsights.tripInsights.trafficEstimate}`;
      }

      if (aiInsights.tips && aiInsights.tips.length > 0) {
        quoteText += `
Tips:`;
        aiInsights.tips.forEach(tip => {
          quoteText += `
• ${tip}`;
        });
      }
    }

    quoteText += `

✦ WHAT'S INCLUDED ✦
• 2025 Audi Q7 Black Optic SUV
• Professional Licensed Chauffeur
• Complimentary Bottled Water
• Complimentary High Speed Wi-Fi
• Real-Time Flight Tracking
• Meet & Greet Service
• All Taxes & Fees

───────────────────────────────
BOOK NOW: (210) 296-7409
───────────────────────────────
Premium Luxury • Fair Prices
═══════════════════════════════`;

    return quoteText.trim();
  };

  const handleCopyQuote = async () => {
    if (!quote) {
      setCopyStatus('Please calculate quote first');
      setTimeout(() => setCopyStatus(''), 3000);
      return;
    }

    try {
      await navigator.clipboard.writeText(generateQuoteText());
      setCopyStatus('Quote copied! ✓');
      setTimeout(() => setCopyStatus(''), 3000);
    } catch {
      setCopyStatus('Copy failed');
      setTimeout(() => setCopyStatus(''), 3000);
    }
  };

  // Helper function to generate quote card as blob - simplified for mobile
  const generateQuoteCardBlob = async (): Promise<Blob | null> => {
    if (!quoteCardRef.current) {
      console.error('Quote card ref not found');
      return null;
    }
    
    try {
      const canvas = await html2canvas(quoteCardRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        logging: true,
        useCORS: false,
        allowTaint: true,
        foreignObjectRendering: false,
      });
      
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png', 0.9);
      });
      
      return blob;
    } catch (error) {
      console.error('html2canvas error:', error);
      return null;
    }
  };

  // Download quote card as image - Mobile compatible
  const handleDownloadQuote = async () => {
    if (!quote) {
      setCopyStatus('Please calculate quote first');
      setTimeout(() => setCopyStatus(''), 3000);
      return;
    }

    setIsDownloading(true);
    setCopyStatus('Generating image...');
    
    try {
      const blob = await generateQuoteCardBlob();
      
      if (!blob) {
        setCopyStatus('Image failed - copying text instead');
        setTimeout(() => {
          handleCopyQuote();
          setIsDownloading(false);
        }, 500);
        return;
      }
      
      const dateStr = quote.pickupDate || new Date().toISOString().split('T')[0];
      const fileName = `RicksBlackCar_Quote_${dateStr}.png`;
      
      // Try Web Share API first (works best on mobile)
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], fileName, { type: 'image/png' });
          const shareData = { files: [file] };
          
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            setCopyStatus('Quote saved! ✓');
            setTimeout(() => setCopyStatus(''), 3000);
            setIsDownloading(false);
            return;
          }
        } catch (shareError) {
          if ((shareError as Error).name === 'AbortError') {
            setCopyStatus('');
            setIsDownloading(false);
            return;
          }
        }
      }
      
      // Fallback: Create download link
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setCopyStatus('Quote downloaded! ✓');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      setTimeout(() => setCopyStatus(''), 3000);
      
    } catch (error) {
      console.error('Download error:', error);
      setCopyStatus('Copying text instead...');
      setTimeout(() => handleCopyQuote(), 500);
    } finally {
      setIsDownloading(false);
    }
  };

  // Share quote card as IMAGE using Web Share API
  const handleShareQuote = async () => {
    if (!quote) {
      setCopyStatus('Please calculate quote first');
      setTimeout(() => setCopyStatus(''), 3000);
      return;
    }

    setIsDownloading(true);
    setCopyStatus('Preparing to share...');
    
    try {
      const blob = await generateQuoteCardBlob();
      
      if (!blob) {
        setCopyStatus('Sharing as text...');
        if (navigator.share) {
          await navigator.share({
            title: "Rick's Black Car Service Quote",
            text: `Quote: $${quote.total.toFixed(2)} (gratuity included)\n\n${quote.pickupAddress || 'San Antonio'} → ${quote.dropoffAddress}\n${quote.pickupDate} at ${quote.pickupTime}\n\nBook: (210) 296-7409`,
          });
          setCopyStatus('Quote shared! ✓');
        } else {
          handleCopyQuote();
        }
        setTimeout(() => setCopyStatus(''), 3000);
        setIsDownloading(false);
        return;
      }
      
      const dateStr = quote.pickupDate || new Date().toISOString().split('T')[0];
      const fileName = `RicksBlackCar_Quote_${dateStr}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      
      // Try sharing with image
      if (navigator.share && navigator.canShare) {
        const shareData = {
          title: "Rick's Black Car Service Quote",
          text: `Your quote: $${quote.total.toFixed(2)} - Book Now: (210) 296-7409`,
          files: [file],
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          setCopyStatus('Quote shared! ✓');
          setTimeout(() => setCopyStatus(''), 3000);
          setIsDownloading(false);
          return;
        }
      }
      
      // Fallback: Share text only
      if (navigator.share) {
        await navigator.share({
          title: "Rick's Black Car Service Quote",
          text: `Quote: $${quote.total.toFixed(2)} (gratuity included)\n\n${quote.pickupAddress || 'San Antonio'} → ${quote.dropoffAddress}\n${quote.pickupDate} at ${quote.pickupTime}\n\nBook: (210) 296-7409`,
        });
        setCopyStatus('Quote shared! ✓');
      } else {
        handleCopyQuote();
      }
      setTimeout(() => setCopyStatus(''), 3000);
      
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        setCopyStatus('');
      } else {
        console.error('Share error:', error);
        handleCopyQuote();
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <SuccessSparkles show={showSuccessAnimation} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-white">Share Your Quote</h3>
          <p className="text-sm text-zinc-400">Copy and send via email or text</p>
        </div>

        {/* Main Quote Card */}
        <div 
          ref={quoteCardRef}
          className="relative bg-gradient-to-b from-zinc-950 to-zinc-900 rounded-xl shadow-2xl border border-yellow-600/30 overflow-hidden"
        >
          {/* Gold accent bar at top */}
          <div className="h-1.5 bg-gradient-to-r from-yellow-700 via-yellow-500 to-yellow-700" />
          
          <div className="p-6">
            {/* Company Header with Text-Based Logo */}
            <div className="text-center mb-6">
              {/* Text-Based Logo Badge */}
              <div className="flex justify-center mb-3">
                <div className="w-20 h-20 rounded-full border-2 border-yellow-600/50 shadow-lg shadow-yellow-900/30 bg-gradient-to-br from-zinc-800 to-black flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-yellow-500 text-2xl font-bold">R</div>
                    <div className="text-yellow-600/70 text-[8px] tracking-wider">BLACK CAR</div>
                  </div>
                </div>
              </div>
              <h2 className="text-xl font-bold text-white tracking-wide">
                Rick's Black Car Service
              </h2>
              <p className="text-sm text-zinc-400 tracking-widest uppercase mt-1">
                Executive Transportation
              </p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Car className="w-4 h-4 text-yellow-500" />
                <span className="text-xs text-yellow-500 font-medium">
                  2025 AUDI Q7 BLACK OPTIC SUV
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-700 my-4" />

            {/* Pickup & Destination */}
            <div className="space-y-4 mb-6">
              {/* Pickup */}
              <div className="flex items-start gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] text-zinc-500 uppercase tracking-widest font-semibold mb-0.5">From:</p>
                  <p className="text-white text-[13px] font-medium leading-[1.3] break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {quote.pickupAddress || 'San Antonio, TX'}
                  </p>
                </div>
              </div>

              {/* Destination */}
              <div className="flex items-start gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] text-zinc-500 uppercase tracking-widest font-semibold mb-0.5">To:</p>
                  <p className="text-white text-[13px] font-medium leading-[1.3] break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {quote.dropoffAddress}
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-700 my-4" />

            {/* Date/Time/Distance/Travel Time Grid - 2x2 */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Date</p>
                <p className="text-white font-semibold text-sm">{formatDate(quote.pickupDate)}</p>
              </div>
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Pickup Time</p>
                <p className="text-white font-semibold text-sm">{formatTime(quote.pickupTime)}</p>
              </div>
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Distance</p>
                <p className="text-yellow-500 font-bold text-lg">{quote.distance.toFixed(1)} mi</p>
              </div>
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Travel Time</p>
                <p className="text-yellow-500 font-bold text-lg">{Math.round(quote.duration)} min</p>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-700 my-4" />

            {/* Total - Large Gold Price with Full Breakdown */}
            <div className="text-center mb-6">
              <p className="text-xs text-zinc-500 uppercase tracking-widest mb-3">Price Breakdown</p>
              
              {/* Detailed Breakdown */}
              <div className="space-y-1 text-sm mb-4">
                {(quote.pricingMethod === 'flat-rate' || quote.pricingMethod === 'local') ? (
                  <div className="flex justify-between px-4">
                    <span className="text-zinc-400">Flat Rate Service:</span>
                    <span className="text-white">${(quote.baseFare ?? quote.total).toFixed(2)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between px-4">
                      <span className="text-zinc-400">Base Service:</span>
                      <span className="text-white">$50.00</span>
                    </div>
                    {quote.mileageCost != null && quote.mileageCost > 0 && (
                      <div className="flex justify-between px-4">
                        <span className="text-zinc-400">Mileage ({quote.distance.toFixed(1)} mi × $3.25):</span>
                        <span className="text-white">${quote.mileageCost.toFixed(2)}</span>
                      </div>
                    )}
                    {quote.timeCost != null && quote.timeCost > 0 && (
                      <div className="flex justify-between px-4">
                        <span className="text-zinc-400">Time ({Math.round(quote.duration)} min × $1.25):</span>
                        <span className="text-white">${quote.timeCost.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}
                {quote.additionalStopsCost != null && quote.additionalStopsCost > 0 && (
                  <div className="flex justify-between px-4">
                    <span className="text-zinc-400">Additional Stops ({Math.round(quote.additionalStopsCost / 25)} × $25):</span>
                    <span className="text-white">${quote.additionalStopsCost.toFixed(2)}</span>
                  </div>
                )}
                {quote.lateNightSurcharge != null && quote.lateNightSurcharge > 0 && (
                  <div className="flex justify-between px-4">
                    <span className="text-amber-400">Late-Night Surcharge:</span>
                    <span className="text-amber-400">+$35.00</span>
                  </div>
                )}
                
                {/* Subtotal line */}
                <div className="flex justify-between px-4 border-t border-zinc-700 pt-2 mt-2">
                  <span className="text-zinc-400">Subtotal:</span>
                  <span className="text-white">${((quote.total - (quote.gratuity ?? 0))).toFixed(2)}</span>
                </div>
                
                {quote.gratuity != null && quote.gratuity > 0 && (
                  <div className="flex justify-between px-4">
                    <span className="text-zinc-400">Gratuity (20%):</span>
                    <span className="text-white">${quote.gratuity.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="border-t border-zinc-700 pt-3">
                <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">Total</p>
                <div className="mb-3">
                  <p className="text-5xl font-bold text-yellow-500">
                    ${quote.total.toFixed(2)}
                  </p>
                </div>
                {quote.gratuityIncluded ? (
                  <span className="inline-block px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-[8px] text-emerald-400 font-semibold mt-1.5">
                    ✓ 20% GRATUITY INCLUDED
                  </span>
                ) : (
                  <span className="inline-block px-2 py-1 bg-zinc-700/50 border border-zinc-600/30 rounded-full text-[8px] text-zinc-400 font-semibold mt-1.5">
                    20% GRATUITY INCLUDED IN TOTAL
                  </span>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-700 my-4" />

            {/* What's Included - with gold star bullets */}
            <div className="bg-zinc-800/50 rounded-lg p-4 mb-4 border border-zinc-700/50">
              <p className="text-xs text-zinc-400 uppercase tracking-widest text-center mb-3 font-semibold">
                What's Included
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {AMENITIES.map((amenity) => (
                  <div key={amenity} className="flex items-center gap-2">
                    <span className="text-yellow-500 text-sm">✦</span>
                    <span className="text-zinc-300 text-sm">{amenity}</span>
                  </div>
                ))}
              </div>
              {/* High Speed Wi-Fi - Centered, Full Width */}
              <div className="flex items-center justify-center gap-2 pt-2 border-t border-zinc-700/50">
                <Wifi className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-500 text-sm font-semibold">✦ High Speed Wi-Fi</span>
              </div>
            </div>

            {/* Weather Section (if available) */}
            {weather && (
              <div className="bg-blue-950/30 rounded-lg p-4 mb-4 border border-blue-500/20">
                <p className="text-xs text-blue-400 uppercase tracking-widest text-center mb-3 font-semibold">
                  ☁️ Pickup Weather Conditions
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-zinc-400">Temperature:</span>
                    <span className="text-white ml-2">{weather.temperature}°F</span>
                  </div>
                  <div>
                    <span className="text-zinc-400">Feels Like:</span>
                    <span className="text-white ml-2">{weather.feelsLike}°F</span>
                  </div>
                  <div>
                    <span className="text-zinc-400">Conditions:</span>
                    <span className="text-white ml-2">{weather.description}</span>
                  </div>
                  <div>
                    <span className="text-zinc-400">Humidity:</span>
                    <span className="text-white ml-2">{weather.humidity}%</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-zinc-400">Wind:</span>
                    <span className="text-white ml-2">{weather.wind}</span>
                  </div>
                </div>
              </div>
            )}

            {/* AI Insights Section (if available) */}
            {aiInsights && (
              <div className="bg-purple-950/30 rounded-lg p-4 mb-4 border border-purple-500/20">
                <p className="text-xs text-purple-400 uppercase tracking-widest text-center mb-3 font-semibold flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  AI Trip Insights
                </p>
                <div className="space-y-2 text-sm">
                  {aiInsights.pricingAnalysis?.explanation && (
                    <div>
                      <span className="text-zinc-400">Pricing:</span>
                      <span className="text-white ml-2">{aiInsights.pricingAnalysis.explanation}</span>
                    </div>
                  )}
                  {aiInsights.tripInsights?.routeNotes && (
                    <div>
                      <span className="text-zinc-400">Route:</span>
                      <span className="text-white ml-2">{aiInsights.tripInsights.routeNotes}</span>
                    </div>
                  )}
                  {aiInsights.tripInsights?.trafficEstimate && (
                    <div>
                      <span className="text-zinc-400">Traffic:</span>
                      <span className="text-white ml-2">{aiInsights.tripInsights.trafficEstimate}</span>
                    </div>
                  )}
                  {aiInsights.tips && aiInsights.tips.length > 0 && (
                    <div>
                      <span className="text-zinc-400">Tips:</span>
                      <ul className="mt-1 ml-4 list-disc text-white">
                        {aiInsights.tips.map((tip, idx) => (
                          <li key={idx}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-zinc-700 my-4" />

            {/* Footer with Phone */}
            <div className="text-center">
              <a 
                href="tel:+12102967409" 
                className="text-yellow-500 font-bold text-lg hover:text-yellow-400 transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                (210) 296-7409
              </a>
              <p className="text-xs text-zinc-500 mt-2">Premium Luxury • Fair Prices</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 space-y-3">
          {/* Book This Quote Button - Primary CTA */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <Button
              onClick={() => setShowBookingDialog(true)}
              className="w-full py-6 text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book This Quote
            </Button>
          </motion.div>

          {/* Share/Download Buttons Row */}
          <div className="grid grid-cols-3 gap-2">
            {/* Save Image Button */}
            <button
              onClick={handleDownloadQuote}
              disabled={isDownloading}
              className="py-3 rounded-xl font-semibold transition-all text-xs bg-gradient-to-r from-blue-700 via-blue-600 to-blue-700 hover:from-blue-600 hover:via-blue-500 hover:to-blue-600 text-white shadow-lg shadow-blue-900/30 hover:shadow-blue-600/40 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-wider flex flex-col items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="text-[10px]">Save Image</span>
            </button>
            
            {/* Copy Text Button */}
            <button
              onClick={handleCopyQuote}
              disabled={isDownloading}
              className="py-3 rounded-xl font-semibold transition-all text-xs bg-gradient-to-r from-yellow-700 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-500 hover:to-yellow-600 text-black shadow-lg shadow-yellow-900/30 hover:shadow-yellow-600/40 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-wider flex flex-col items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Copy className="w-5 h-5" />
              <span className="text-[10px]">Copy Text</span>
            </button>
            
            {/* Share Image Button */}
            <button
              onClick={handleShareQuote}
              disabled={isDownloading}
              className="py-3 rounded-xl font-semibold transition-all text-xs bg-gradient-to-r from-green-700 via-green-600 to-green-700 hover:from-green-600 hover:via-green-500 hover:to-green-600 text-white shadow-lg shadow-green-900/30 hover:shadow-green-600/40 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-wider flex flex-col items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Share2 className="w-5 h-5" />
              )}
              <span className="text-[10px]">Share Image</span>
            </button>
          </div>

          {/* Helper Text */}
          <p className="text-center text-[10px] text-zinc-500">
            📱 Mobile: Use "Share Image" to text or email the quote card
          </p>

          {/* Status Message */}
          {copyStatus && (
            <div className={`text-center text-sm font-medium animate-fade-in ${
              copyStatus.includes('copied') || copyStatus.includes('downloaded') || 
              copyStatus.includes('shared') || copyStatus.includes('saved') || 
              copyStatus.includes('ready') || copyStatus.includes('opened') || copyStatus.includes('✓')
                ? 'text-emerald-400' 
                : 'text-yellow-400'
            }`}>
              {copyStatus}
            </div>
          )}
        </div>
      </motion.div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-yellow-600/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-yellow-500 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Book This Quote
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Enter your contact details and we'll confirm your booking shortly.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleBookingSubmit} className="space-y-4 mt-4">
            {/* Quote Summary */}
            <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-zinc-400">Route</span>
                <span className="text-sm text-white font-medium truncate max-w-[200px]">
                  {quote.pickupAddress?.split(',')[0]} → {quote.dropoffAddress?.split(',')[0]}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-400">Total</span>
                <span className="text-lg text-yellow-500 font-bold">${quote.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="booking-name" className="text-zinc-300 flex items-center gap-2">
                <User className="w-4 h-4 text-yellow-500" />
                Full Name *
              </Label>
              <Input
                id="booking-name"
                value={bookingForm.name}
                onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-yellow-500"
                maxLength={100}
                required
              />
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="booking-email" className="text-zinc-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-yellow-500" />
                Email Address *
                {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email) && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 15 }}
                  >
                    <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_4px_rgba(52,211,153,0.6)]" />
                  </motion.div>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="booking-email"
                  type="email"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  className={`bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 pr-10 ${
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email) 
                      ? 'border-emerald-500/50 focus:border-emerald-500' 
                      : bookingForm.email.length > 0 
                        ? 'border-amber-500/50 focus:border-amber-500' 
                        : 'focus:border-yellow-500'
                  }`}
                  maxLength={255}
                  required
                />
                {bookingForm.email.length > 0 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email) ? (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 400, damping: 12 }}
                      >
                        <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                      </motion.div>
                    ) : (
                      <span className="text-xs text-amber-400">
                        Invalid
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <Label htmlFor="booking-phone" className="text-zinc-300 flex items-center gap-2">
                <Phone className="w-4 h-4 text-yellow-500" />
                Phone Number *
                {bookingForm.phone.replace(/\D/g, '').length === 10 && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 15 }}
                  >
                    <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_4px_rgba(52,211,153,0.6)]" />
                  </motion.div>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="booking-phone"
                  type="tel"
                  value={bookingForm.phone}
                  onChange={handlePhoneChange}
                  placeholder="(210) 555-1234"
                  className={`bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 pr-10 ${
                    bookingForm.phone.replace(/\D/g, '').length === 10 
                      ? 'border-emerald-500/50 focus:border-emerald-500' 
                      : bookingForm.phone.length > 0 
                        ? 'border-amber-500/50 focus:border-amber-500' 
                        : 'focus:border-yellow-500'
                  }`}
                  maxLength={14}
                  required
                />
                {bookingForm.phone.length > 0 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {bookingForm.phone.replace(/\D/g, '').length === 10 ? (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 400, damping: 12 }}
                      >
                        <Check className="w-4 h-4 text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                      </motion.div>
                    ) : (
                      <span className="text-xs text-amber-400">
                        {bookingForm.phone.replace(/\D/g, '').length}/10
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            {(() => {
              const isNameValid = bookingForm.name.trim().length > 0;
              const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email);
              const isPhoneValid = bookingForm.phone.replace(/\D/g, '').length === 10;
              const isFormValid = isNameValid && isEmailValid && isPhoneValid;
              
              return (
                <motion.div
                  animate={isFormValid ? {
                    scale: [1, 1.02, 1],
                    transition: { duration: 0.3 }
                  } : {}}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-6 text-base font-bold transition-all duration-300 disabled:opacity-50 ${
                      isFormValid 
                        ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-500/30' 
                        : 'bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500 text-black'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending Request...
                      </>
                    ) : isFormValid ? (
                      <motion.span
                        className="flex items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Check className="w-5 h-5 mr-2" />
                        Ready to Submit
                      </motion.span>
                    ) : (
                      <>
                        <Calendar className="w-5 h-5 mr-2" />
                        Confirm Booking Request
                      </>
                    )}
                  </Button>
                </motion.div>
              );
            })()}

            <p className="text-xs text-zinc-500 text-center">
              A team member will review your request and contact you to confirm availability.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LuxuryQuotePreviewCard;